import React, { useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Empty, Flex, Typography } from "antd";
import { ReloadOutlined, CheckCircleOutlined } from "@ant-design/icons";
import { SbButton, SbCard, SbCheckbox, SbEmpty, SbSpin, SbNotification, SbTag } from "uiCore";
import { AuthContext } from "State/AuthContext";
import PageHeader from "Components/PageHeader";
import { TableWrapperStyled } from "styles/common.styles";
import { PageTransitionStyled } from "./talentMobility.animations";
import {
  SummaryBarStyled,
  StepChainStyled,
  StepNodeStyled,
  StepArrowStyled,
  OpenPositionsHeadingStyled,
  PathwayFilterRowStyled,
} from "./TalentMobilityEmployeeV2.styles";
import AspirationCaptureV2 from "./AspirationCaptureV2";
import PositionCard from "./components/PositionCard";
import useTalentMobilitySettingsStore, { refreshThresholdsFromApi } from "Store/useTalentMobilitySettingsStore";
import {
  createTalentMobilityCareerPaths,
  getTalentMobilityCareerPathsSmiStatus,
} from "Api/apiService";
import { useSavedCareerPaths } from "./useTalentMobilityData";
import { getOrgLocationNames } from "./orgLocations.utils";
import {
  getDiscoverablePositions,
  selectTrackPositions,
  sortPositionsBySmi,
  mapSavedCareerPathsToAspiration,
  toCareerPathPlacementFilter,
} from "./careerPathways.utils";

import { TOUR_MODULE_KEY, CROSS_PAGE_TOUR_PHASE, getTourSteps } from "./TalentMobilityEmployeeV2.constants";
import TalentMobilityIcon from "Components/SBLayoutIcon/TalentMobilityIcon";

// Guided tour — reuses the shared SbTour helpers
import SbTour from "uiCore/SbTour/SbTour";
import useModuleTour from "uiCore/SbTour/useModuleTour";
import buildTourSteps from "uiCore/SbTour/buildTourSteps";
import useTourStore from "Store/useTourStore";

import useAnalytics from "Hooks/useAnalytics";
import { ANALYTICS_EVENTS } from "Utils/analytics";
const { Text } = Typography;

// Track tags arrive as the backend's label strings (see
// career_paths_service._TAG_LABELS), so each checkbox carries the exact tag
// it filters on. OR logic across whichever boxes are checked — Within/Cross
// Function are mutually exclusive per track, so AND would often return
// nothing.
const PATHWAY_FILTER_OPTIONS = [
  { key: "withinFunction", label: "Within Function", tag: "WITHIN_FUNCTION" },
  { key: "crossFunction", label: "Cross Function", tag: "CROSS_FUNCTION" },
];

// Card-header chip color for within-function tracks (the only tag shown).
const TRACK_TAG_COLORS = { WITHIN_FUNCTION: "blue" };

// req #5 — full progression chain (current → intermediate steps → target).
// The API track's roles already run current-first to target-last, and the
// backend omits tenure years by design, so nodes are role names only.
const StepChain = ({ roles }) => (
  <StepChainStyled>
    {roles.map((role, i) => {
      const isLast = i === roles.length - 1;
      return (
        <React.Fragment key={`${role.name}-${i}`}>
          <StepNodeStyled $current={i === 0} $target={isLast}>
            {role.name}
          </StepNodeStyled>
          {!isLast && <StepArrowStyled>→</StepArrowStyled>}
        </React.Fragment>
      );
    })}
  </StepChainStyled>
);

const ANALYTICS_FROM = "talent_mobility_employee";
const ANALYTICS_EVENT = ANALYTICS_EVENTS.TALENT_MOBILITY.EMPLOYEE_VIEW_OPENED;

const TalentMobilityEmployeeV2 = () => {
  const track = useAnalytics(ANALYTICS_FROM);
  // This screen has no Mixpanel coverage of its own; the automatic
  // pageview only records the URL, not that the module was reached.
  useEffect(() => {
    track(ANALYTICS_EVENT);
  }, [track]);
  // Read the primitive directly instead of via useIsOpenRoleVisible(), which
  // would also attach its own guarded (once-per-session) hydrate effect
  // alongside the unconditional one below — this page fetches thresholds
  // itself, so that second trigger would just be a duplicate GET race on
  // whichever mount happens to be first in the session.
  const isOpenRoleVisible = useTalentMobilitySettingsStore((state) => state.isOpenRoleVisible);
  const { orgMetaData } = useContext(AuthContext);
  // All page state is local — no client-side store. The backend's
  // talent_mobility_aspiration / talent_mobility_career_pathway tables are
  // the single source of truth: every visit rehydrates from
  // POST /career-paths (empty payload), so nothing needs to outlive this
  // component.
  // aspiration: { targetRoleId, targetRoleTitle, mobile, locations,
  // currentFunction, ...org placement } — null until hydrated or captured.
  const [aspiration, setAspiration] = useState(null);
  const [careerPaths, setCareerPaths] = useState(null); // POST /career-paths response
  const [careerPathsLoading, setCareerPathsLoading] = useState(false);
  const [editingAspiration, setEditingAspiration] = useState(false);
  const [pathwayFilters, setPathwayFilters] = useState([]);
  const [refreshingSmiJdIds, setRefreshingSmiJdIds] = useState(() => new Set());
  // Monotonic id for the background POST /career-paths continuation — a
  // re-save while one is in flight bumps it, so only the latest request may
  // write its response into careerPaths.
  const careerPathsRequestSeq = useRef(0);
  // In-flight continuations must die with the page: the save/refresh error
  // toasts are global (SbNotification.error sits until dismissed), so a
  // request that fails after navigation or logout would otherwise toast over
  // whatever screen replaced this one — including the login screen. Bumping
  // the seq invalidates the save continuation the same way a re-save does.
  const isMountedRef = useRef(true);
  useEffect(() => {
    // Reset on every run so a remounted effect (e.g. StrictMode's dev
    // double-invoke) doesn't leave the flag stuck false — same pattern as
    // useTalentMobilityEmployees' isMountedRef.
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      careerPathsRequestSeq.current += 1;
    };
  }, []);

  // GET /talent-mobility/thresholds, every visit — same "every visit
  // rehydrates" contract as career-paths below, rather than the
  // once-per-session guard other Talent Mobility screens use
  // (useMatchThresholds/useIsOpenRoleVisible/useSuccessionThresholds), since
  // this page's SMI/match colors need the org's latest banding on load.
  // refreshThresholdsFromApi writes straight into the Zustand store, so it's
  // safe to let it resolve after unmount — no isMountedRef guard needed,
  // same as its other call site below in handleAspirationSaved.
  useEffect(() => {
    refreshThresholdsFromApi().catch(() => {
      // Already logged by getTalentMobilityThresholds; store just keeps
      // whatever thresholds it already had.
    });
  }, []);

  // Every visit rehydrates from the backend: POST /career-paths with an
  // EMPTY payload returns the saved aspiration's pathway plus
  // target_role_id / target_role_title / locations (200), or null when no
  // aspiration was ever saved (404) — then the "Tell us where you want to
  // go" capture form shows instead. A real hydration failure (hydrationFailed
  // below) shows a retry state instead of the capture form — see the comment
  // above its render branch for why.
  const {
    saved: savedCareerPaths,
    isLoading: hydrating,
    error: hydrationFailed,
    refetch: retryHydration,
  } = useSavedCareerPaths();
  // A 200 without a target_role_id can't seed the summary bar — treat it
  // like "nothing saved" so the capture form shows instead of an empty
  // pathways view with a blank target.
  const hasSavedAspiration = savedCareerPaths?.target_role_id != null;

  useEffect(() => {
    if (savedCareerPaths?.target_role_id == null) return;
    setAspiration(mapSavedCareerPathsToAspiration(savedCareerPaths));
    setCareerPaths(savedCareerPaths);
  }, [savedCareerPaths]);

  // A hydration failure (500 / timeout — anything but the 404 "never
  // saved") must NOT fall back to the blank capture form: Save & Continue
  // POSTs to the same aspiration record, so silently showing an empty form
  // here would let someone who merely hit a transient error overwrite their
  // real saved aspiration with no warning. hydrationFailed (see
  // useSavedCareerPaths) renders a retry state instead, below.

  // Save & Continue lands on the pathways view IMMEDIATELY — the form's
  // values render the summary bar right away (careerPathsLoading keeps the
  // track list on its spinner) and POST /career-paths resolves in the
  // background, filling the tracks in. This parent stays mounted across the
  // form → pathways switch, so plain setState carries the continuation.
  const handleAspirationSaved = (saved) => {
    setAspiration(saved);
    setEditingAspiration(false);
    setCareerPaths(null);
    setCareerPathsLoading(true);

    // The current role is NOT sent — the backend resolves it from the JWT.
    // Whenever the employee is open to relocation the selected locations go
    // in the payload — including "All Locations", which sends the full list
    // rather than omitting the key (per requirement, so the saved aspiration
    // carries the explicit list). Only "No relocation" omits locations.
    // target_role_id is NOT sent — POST /career-paths has no id-based mode,
    // it resolves the target role by target_role_name (+ the org placement
    // filters below) instead (see createTalentMobilityCareerPaths). An
    // "ALL - All" placement pick goes out as null — no filter at that level —
    // since the backend can't match the sentinel text itself.
    const payload = {
      target_role_name: saved.targetRoleTitle,
      business_unit: toCareerPathPlacementFilter(saved.businessUnit),
      function: toCareerPathPlacementFilter(saved.functionDepartment),
      sub_function: toCareerPathPlacementFilter(saved.subFunction),
      relocate: !!saved.mobile,
    };
    if (saved.mobile && saved.locations.length) payload.locations = saved.locations;

    const requestId = ++careerPathsRequestSeq.current;
    (async () => {
      try {
        // Thresholds refresh in parallel so the pathway cards' SMI colors use
        // the org's latest banding; its failure must not block the save.
        const [paths] = await Promise.all([
          createTalentMobilityCareerPaths(payload),
          refreshThresholdsFromApi().catch(() => null),
        ]);
        if (requestId !== careerPathsRequestSeq.current) return;
        setCareerPaths(paths);
        setCareerPathsLoading(false);
      } catch (error) {
        if (requestId !== careerPathsRequestSeq.current) return;
        setCareerPathsLoading(false);
        SbNotification.error(
          "Unable to generate career paths",
          error?.response?.data?.message || "Please try again."
        );
      }
    })();
  };

  // A card's refresh button — GET /career-paths/smi-status for that ONE
  // position. A stored score comes back as status "ready" and is written
  // onto careerPaths in place (no full re-fetch); every other status
  // ("started" / "in_progress" / "restarted") surfaces the backend's own
  // message and the card keeps its loading state. The Set keys the spinner
  // per position, so refreshing one card doesn't spin the others.
  const refreshPositionSmi = async (jdSbUniqueId) => {
    if (refreshingSmiJdIds.has(jdSbUniqueId)) return;
    setRefreshingSmiJdIds((prev) => new Set(prev).add(jdSbUniqueId));
    try {
      const smiStatus = await getTalentMobilityCareerPathsSmiStatus(jdSbUniqueId);
      if (!isMountedRef.current) return;
      if (smiStatus?.smi_score != null) {
        // Functional update so a slow response can't clobber careerPaths
        // changes made while it was in flight.
        setCareerPaths((prev) =>
          prev
            ? {
                ...prev,
                tracks: (prev.tracks || []).map((track) => ({
                  ...track,
                  positions: (track.positions || []).map((position) =>
                    position.jd_sb_unique_id === jdSbUniqueId
                      ? { ...position, smi_score: smiStatus.smi_score, is_smi_loading: false }
                      : position
                  ),
                })),
              }
            : prev
        );
      } else {
        SbNotification.info(
          "SMI calculation",
          smiStatus?.message || "SMI calculation is in progress — please check back shortly."
        );
      }
    } catch (error) {
      if (!isMountedRef.current) return;
      SbNotification.error(
        "Unable to refresh SMI",
        error?.response?.data?.message || "Please try again."
      );
    } finally {
      setRefreshingSmiJdIds((prev) => {
        const next = new Set(prev);
        next.delete(jdSbUniqueId);
        return next;
      });
    }
  };

  // A position's PositionCard props — shared by the cards and the tour, which
  // opens one position's opportunity page exactly as clicking its card does.
  const toPositionRole = (position) => ({
    id: position.jd_sb_unique_id,
    title: position.title || position.jd_sb_unique_id,
    function: position.department,
    location: position.location,
    level: position.level,
    // PositionCard badges key off the mock vocabulary —
    // priority "High", status "Open" — so normalize the API's
    // boolean / job_opening_status to it. These are the path's
    // open positions, so the Open tag follows the admin's
    // "Open Role Visibility" toggle (thresholds API
    // is_open_role_visible) unless a position explicitly
    // reports a non-open status (today's responses send null).
    priority: position.priority ? "High" : undefined,
    status:
      isOpenRoleVisible &&
      !(typeof position.status === "string" && position.status.toLowerCase() !== "open")
        ? "Open"
        : undefined,
  });
  const toMatchPct = (position) => (position.smi_score == null ? null : Math.round(position.smi_score));

  const togglePathwayFilter = (key) =>
    setPathwayFilters((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));

  // Everything below renders the POST /career-paths response — no mock data.
  // Computed ahead of the early returns below (aspiration may still be null
  // there) so the guided tour, whose hooks run on every render, can branch
  // on what the pathways view will show.
  const tracks = careerPaths?.tracks || [];

  // The "Within Function" checkbox shows the employee's own function —
  // currentRole.jdMetadataJson.function from POST /people-profile-details,
  // captured by AspirationCaptureV2 at Save & Continue. A hydrated visit
  // (or a save without it) falls back to looking the current JD's
  // department up among the tracks' positions, then to the generic
  // "Within Function" label.
  const currentJdId = careerPaths?.current_jd_sb_unique_id;
  const currentDepartment =
    aspiration?.currentFunction ||
    (currentJdId
      ? tracks
          .flatMap((track) => track.positions || [])
          .find((position) => position.jd_sb_unique_id === currentJdId)?.department
      : undefined);

  // The department checkbox (ex "Within Function") doesn't match a tag —
  // it keeps tracks with any position in the employee's own department
  // (the current role counts, so its track shows too). The other options
  // still match on the backend's track tags.
  const trackMatchesFilter = (track, key) => {
    if (key === "withinFunction") {
      return (
        Boolean(currentDepartment) &&
        (track.positions || []).some((position) => position.department === currentDepartment)
      );
    }
    const option = PATHWAY_FILTER_OPTIONS.find((opt) => opt.key === key);
    return Boolean(option) && (track.tags || []).includes(option.tag);
  };

  // OR logic across checked filters; no boxes checked = show everything.
  const filteredTracks = pathwayFilters.length
    ? tracks.filter((track) => pathwayFilters.some((key) => trackMatchesFilter(track, key)))
    : tracks;

  const pathwayTracks = filteredTracks
    .map((track) => {
      // The position list mirrors WHY this track is showing (OR across the
      // checkboxes): matched via the department box → that department's
      // openings; matched via a tag box, or no boxes checked → the track's
      // next-step opening (the first position along the path that isn't the
      // employee's current role — the backend drops non-discoverable
      // positions from the array entirely, so it's identified by
      // current_jd_sb_unique_id, never by index; see careerPathways.utils).
      // A track matching both checkboxes shows both sets, then ranked
      // best-match-first below (not path order — see sortPositionsBySmi).
      const matchedDepartment =
        pathwayFilters.includes("withinFunction") && trackMatchesFilter(track, "withinFunction");
      const matchedTagFilter = pathwayFilters.some(
        (key) => key !== "withinFunction" && trackMatchesFilter(track, key)
      );
      const positions = sortPositionsBySmi(
        selectTrackPositions({
          positions: getDiscoverablePositions(track),
          currentJdId,
          showDepartmentPositions: matchedDepartment,
          currentDepartment,
          showNextStep: !pathwayFilters.length || matchedTagFilter,
        })
      );
      return { track, positions };
    })
    // Highest-SMI path first — ranked by its own best (now first, after the
    // sort above) open position, same null-safe -1 floor so a track with
    // nothing scored yet sinks to the bottom instead of leading.
    .sort((a, b) => (b.positions[0]?.smi_score ?? -1) - (a.positions[0]?.smi_score ?? -1));

  // ── Guided tour ────────────────────────────────────────────────────────────
  // Shared tour wiring: controller + seen-flag + auto-launch + global-button
  // registration. Auto-launch is suppressed while a cross-page hop is in flight.
  const navigate = useNavigate();
  const crossPageTourPhase = useTourStore((s) => s.crossPageTourPhase);
  const setCrossPageTourPhase = useTourStore((s) => s.setCrossPageTourPhase);
  const clearCrossPageTourPhase = useTourStore((s) => s.clearCrossPageTourPhase);
  const {
    open: tourOpen,
    current: tourStep,
    remountKey: tourRunId,
    begin,
    goToStep: goToTourStep,
    close: closeMainTour,
    markSeen,
  } = useModuleTour({ moduleKey: TOUR_MODULE_KEY, autoLaunch: !crossPageTourPhase });

  const tourTargetRefs = {
    careerTarget: useRef(null),
    targetRole: useRef(null),
    relocation: useRef(null),
    saveContinue: useRef(null),
    summaryBar: useRef(null),
    pathwayFilters: useRef(null),
    pathwayCard: useRef(null),
    openPositions: useRef(null),
    matchScore: useRef(null),
    apply: useRef(null),
    noPathways: useRef(null),
  };

  // The tour's pathway steps all point at one pathway: the first (best-ranked)
  // one with an open position, so the position steps have a card to show.
  const isAspirationForm = !aspiration || editingAspiration;
  const tourSectionIndex = Math.max(0, pathwayTracks.findIndex(({ positions }) => positions.length > 0));
  const tourPosition = pathwayTracks[tourSectionIndex]?.positions[0];
  const branchTourSteps = getTourSteps({
    isAspirationForm,
    hasPathways: pathwayTracks.length > 0,
    hasPositions: Boolean(tourPosition),
  });
  // The Skill Gap step shows on the opportunity page (after the hop), not here.
  const tourSteps = branchTourSteps
    .filter((step) => !step.page)
    .map((step) => {
      if (!step.key && step.formPrimaryText && isAspirationForm) return { ...step, primaryText: step.formPrimaryText };
      if (step.filteredDescription && pathwayFilters.length) return { ...step, description: step.filteredDescription };
      return step;
    });

  const closeTour = () => {
    markSeen();
    closeMainTour();
  };

  // Finish greeting primary → bring the pathways (or, on the form, the Set
  // Career Target card) into view.
  const finishTour = () => {
    markSeen();
    closeMainTour();
    const focusRef = isAspirationForm
      ? tourTargetRefs.careerTarget
      : pathwayTracks.length
        ? tourTargetRefs.pathwayCard
        : tourTargetRefs.noPathways;
    focusRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // Advancing onto the Skill Gap step opens the tour's position, exactly as
  // clicking its card would; that page hands back to resume at Apply.
  const changeTourStep = (step) => {
    const nextSpecStep = branchTourSteps[branchTourSteps.indexOf(tourSteps[tourStep]) + 1];
    if (step > tourStep && nextSpecStep?.page === CROSS_PAGE_TOUR_PHASE.SKILL_GAP && tourPosition) {
      setCrossPageTourPhase(CROSS_PAGE_TOUR_PHASE.SKILL_GAP);
      navigate(`/talent-mobility-employee/opportunity/${tourPosition.jd_sb_unique_id}`, {
        state: {
          role: toPositionRole(tourPosition),
          matchPct: toMatchPct(tourPosition),
          skillsToDevelop: tourPosition.skills_to_develop,
        },
      });
      return;
    }
    goToTourStep(step);
  };

  // Returning from the opportunity page → resume at Apply once the pathways
  // view has rehydrated (not during the in-between renders where it is still
  // deciding which screen to show — those would resolve to the form branch).
  const isSettling = hydrating || (hasSavedAspiration && !aspiration) || careerPathsLoading;
  useEffect(() => {
    if (isSettling) return;
    const resumeAt = tourSteps.findIndex((step) => step.resumePhase && step.resumePhase === crossPageTourPhase);
    if (resumeAt < 0) return;
    clearCrossPageTourPhase();
    begin(resumeAt);
  }, [crossPageTourPhase, isSettling, tourSteps, clearCrossPageTourPhase, begin]);

  const { steps: builtTourSteps, actionsRender: renderTourActions, indicatorsRender: renderTourIndicators } =
    buildTourSteps(tourSteps, {
      resolveTarget: (step) => tourTargetRefs[step.key]?.current,
      greeting: {
        icon: (step, isLast) => (isLast ? <CheckCircleOutlined /> : <TalentMobilityIcon />),
        onPrimary: (step, index, isLast) => (isLast ? finishTour() : changeTourStep(index + 1)),
        onSecondary: () => closeTour(),
      },
    });

  const tour = (
    <SbTour
      key={tourRunId}
      open={tourOpen}
      current={tourStep}
      onChange={changeTourStep}
      steps={builtTourSteps}
      onClose={closeTour}
      actionsRender={renderTourActions}
      indicatorsRender={renderTourIndicators}
    />
  );

  // Don't flash the capture form while the saved-aspiration lookup is still
  // deciding which of the two screens this visit gets (the second clause
  // covers the one render between the response landing and the hydration
  // effect writing it into aspiration).
  if (hydrating || (hasSavedAspiration && !aspiration)) {
    return (
      <PageTransitionStyled>
        <div style={{ display: "flex", justifyContent: "center", padding: "48px 0" }}>
          <SbSpin size="large" />
        </div>
      </PageTransitionStyled>
    );
  }

  // hydrationFailed only ever fires before `aspiration` is set (a failed
  // hydration never reaches the effect that populates it), so this can't
  // intercept the legitimate Edit flow, which requires aspiration to already
  // be truthy.
  if (hydrationFailed && !aspiration) {
    return (
      <PageTransitionStyled>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 16,
            padding: "48px 0",
          }}
        >
          <SbEmpty description="Unable to load your saved career aspiration. Please try again." />
          <SbButton buttonType="primary" icon={<ReloadOutlined />} onClick={retryHydration}>
            Retry
          </SbButton>
        </div>
      </PageTransitionStyled>
    );
  }

  if (isAspirationForm) {
    return (
      <>
        <AspirationCaptureV2
          initialAspiration={aspiration}
          onSaved={handleAspirationSaved}
          tourRefs={tourTargetRefs}
        />
        {tour}
      </>
    );
  }

  // "All" is derived at render time, from the LIVE org metadata (it loads
  // asynchronously, so a value frozen at hydration time would misreport
  // until the next reload) and by membership rather than list length (the
  // saved list can carry locations since removed from org metadata).
  const orgLocations = getOrgLocationNames(orgMetaData);
  const allLocationsSelected =
    orgLocations.length > 0 && orgLocations.every((name) => aspiration.locations.includes(name));

  // Avoid dumping every selected location inline (req: "if the user has
  // selected all, just say All; for multiple selections show the first 3
  // plus a +N more count").
  const PREVIEW_LIMIT = 3;
  const previewList = (items) =>
    items.length > PREVIEW_LIMIT
      ? `${items.slice(0, PREVIEW_LIMIT).join(", ")} +${items.length - PREVIEW_LIMIT} more`
      : items.join(", ");

  const locationSummary = !aspiration.mobile
    ? "Not open to relocation"
    : allLocationsSelected
      ? "All"
      : aspiration.locations.length
        ? previewList(aspiration.locations)
        : "Open to all locations";


  // The tour's pathway steps spotlight the section at tourSectionIndex —
  // wrapped in divs because SbCard doesn't forward a ref.
  const pathwaySections = pathwayTracks.map(({ track, positions }, sectionIndex) => {
    const isTourSection = sectionIndex === tourSectionIndex;
    return (
      // Pathways are always-open sections (no accordion) — SbCard's built-in
      // header bar carries the track name and the body stays permanently
      // visible.
      <div key={track.track_id} ref={isTourSection ? tourTargetRefs.pathwayCard : undefined}>
        <SbCard
          size="small"
          title={
            <Flex align="center" gap={8}>
              {track.track_name}
              {/* Only the within-function chip shows (named after the
                  employee's own department, like the filter checkbox) —
                  cross-function tracks carry no chip by design. */}
              {(track.tags || []).includes("WITHIN_FUNCTION") && currentDepartment && (
                <SbTag color={TRACK_TAG_COLORS.WITHIN_FUNCTION}>{currentDepartment}</SbTag>
              )}
            </Flex>
          }
        >
          <StepChain roles={track.roles || []} />
          <div ref={isTourSection ? tourTargetRefs.openPositions : undefined}>
            {/* The count stands in for the removed accordion's at-a-glance
                summary — the section itself stays permanently open. */}
            <OpenPositionsHeadingStyled>
              Open Positions Linked to This Path ({positions.length})
            </OpenPositionsHeadingStyled>
            {positions.length ? (
              positions.map((position, positionIndex) => (
                <PositionCard
                  key={position.jd_sb_unique_id}
                  onPath
                  opportunityId={position.jd_sb_unique_id}
                  role={toPositionRole(position)}
                  matchPct={toMatchPct(position)}
                  skillsToDevelop={position.skills_to_develop}
                  // No score yet (is_smi_loading) → Apply disabled and the row
                  // inert until a refresh brings the SMI in.
                  isSmiPending={position.smi_score == null && Boolean(position.is_smi_loading)}
                  // Refresh only offered while the backend is still computing
                  // this position's SMI (no smi_score yet, is_smi_loading
                  // true) — scored cards don't need it.
                  onRefreshSmi={
                    position.smi_score == null && position.is_smi_loading
                      ? () => refreshPositionSmi(position.jd_sb_unique_id)
                      : undefined
                  }
                  isRefreshingSmi={refreshingSmiJdIds.has(position.jd_sb_unique_id)}
                  tourRefs={
                    isTourSection && positionIndex === 0
                      ? { matchScore: tourTargetRefs.matchScore, apply: tourTargetRefs.apply }
                      : undefined
                  }
                />
              ))
            ) : (
              <Text type="secondary">No open positions on this path yet.</Text>
            )}
          </div>
        </SbCard>
      </div>
    );
  });

  return (
    <PageTransitionStyled>
      <PageHeader
        title="My Career Pathways"
        subtitle="Each row is one pathway toward your stated career goal, showing its full progression and open positions."
      />

      <SummaryBarStyled ref={tourTargetRefs.summaryBar}>
        <span>
          Target: <strong>{aspiration.targetRoleTitle || "—"}</strong> · Location: <strong>{locationSummary}</strong>
        </span>
        <SbButton buttonType="action" onClick={() => setEditingAspiration(true)}>
          Edit
        </SbButton>
      </SummaryBarStyled>

      <PathwayFilterRowStyled ref={tourTargetRefs.pathwayFilters}>
        <span className="label">Filter pathways:</span>
        {PATHWAY_FILTER_OPTIONS.map((opt) => (
          <SbCheckbox
            key={opt.key}
            checked={pathwayFilters.includes(opt.key)}
            onChange={() => togglePathwayFilter(opt.key)}
          >
            {opt.key === "withinFunction" && currentDepartment ? currentDepartment : opt.label}
          </SbCheckbox>
        ))}
      </PathwayFilterRowStyled>

      {/* PageContainerStyled is height:100% + overflow:hidden, so the track
          list is the page's one scrollable area (same pattern as the other
          V2 pages) — header/summary/filters stay pinned above it. */}
      <TableWrapperStyled style={{ padding: "16px 24px" }}>
        {/* Save & Continue lands here before POST /career-paths resolves —
            careerPathsLoading holds the spinner until the tracks arrive. */}
        {careerPathsLoading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: "48px 0" }}>
            <SbSpin size="large" />
          </div>
        ) : pathwaySections.length ? (
          <Flex vertical gap={12}>
            {pathwaySections}
          </Flex>
        ) : (
          <div ref={tourTargetRefs.noPathways}>
            <SbEmpty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                pathwayFilters.length
                  ? "No pathways match the selected filters."
                  : "No career pathways found for your target role yet."
              }
            />
          </div>
        )}
      </TableWrapperStyled>

      {tour}
    </PageTransitionStyled>
  );
};

export default TalentMobilityEmployeeV2;
